/**
 * Shared feature-flag helpers.
 *
 * Global config supports three states:
 * - `true`: enabled for all users
 * - `false`: disabled globally, but can still be granted via entitlement
 * - `'internal'`: enabled only for internal users
 */

export const INTERNAL_FEATURE_FLAG = 'internal';

export const INTERNAL_FEATURE_EMAIL_DOMAINS = Object.freeze([
  '@profiteia.io',
  '@bakeable.nl',
  '@giga-storage.com',
]);

/**
 * Feature flag keys — these must match the keys the backend may include
 * in a user's `active_entitlements` array for per-user early access.
 */
export const FEATURE_FLAGS = Object.freeze({
  DASHBOARD_BUILDER: 'dashboard-builder',
  KPI_VISUALIZATIONS: 'kpi-visualizations',
  INDICATOR_SETTINGS: 'indicator-settings',
  API_DOCS_CATALOG: 'api-docs-catalog',
  POWER_MARKET_SIMULATOR: 'power-market-simulator',
  ENVIRONMENT_ASSETS: 'environment-assets',
  SHADOW_TRADING: 'shadow-trading',
  FORWARD_BENCHMARK: 'forward-benchmark',
  HEDGING_BENCHMARK: 'hedging-benchmark',
  HEDGING_HISTORICAL_ROUTE: 'hedging-historical-route',
  CAPACITY_FEE_FORECASTER: 'capacity-fee-forecaster',
  SPIKE_PROBABILITY: 'spike-probability',
  REPORTS: 'report-builder',
  WATER_RISK: 'water-risk',
  DISTRIBUTION_INTERRUPTIONS: 'distribution-interruptions',
  CHANGELOG_DIALOG: 'changelog-dialog',
  ALERT_REGISTRY_ALLOWLIST: 'alert-registry-allowlist',
  BIDDING_STRATEGY_SITE_LOAD: 'bidding-strategy-site-load',
  GRID_TARIFF_TIME_OF_USE: 'grid-tariff-time-of-use',
  SHADOW_GRID_CONNECTION: 'shadow-grid-connection',
  SHADOW_SITE: 'shadow-site',
  SHADOW_DEGRADATION: 'shadow-degradation',
});

/**
 * Map from entitlement string → config key so we can look up the global
 * flag for a given feature-flag key.
 * @type {Object<string, string>}
 */
export const ENTITLEMENT_TO_CONFIG = Object.freeze({
  [FEATURE_FLAGS.DASHBOARD_BUILDER]: 'DASHBOARD_BUILDER',
  [FEATURE_FLAGS.KPI_VISUALIZATIONS]: 'KPI_VISUALIZATIONS',
  [FEATURE_FLAGS.INDICATOR_SETTINGS]: 'INDICATOR_SETTINGS',
  [FEATURE_FLAGS.API_DOCS_CATALOG]: 'API_DOCS_CATALOG',
  [FEATURE_FLAGS.POWER_MARKET_SIMULATOR]: 'POWER_MARKET_SIMULATOR',
  [FEATURE_FLAGS.ENVIRONMENT_ASSETS]: 'ENVIRONMENT_ASSETS',
  [FEATURE_FLAGS.SHADOW_TRADING]: 'SHADOW_TRADING',
  [FEATURE_FLAGS.FORWARD_BENCHMARK]: 'FORWARD_BENCHMARK',
  [FEATURE_FLAGS.HEDGING_BENCHMARK]: 'HEDGING_BENCHMARK',
  [FEATURE_FLAGS.HEDGING_HISTORICAL_ROUTE]: 'HEDGING_HISTORICAL_ROUTE',
  [FEATURE_FLAGS.CAPACITY_FEE_FORECASTER]: 'CAPACITY_FEE_FORECASTER',
  [FEATURE_FLAGS.SPIKE_PROBABILITY]: 'SPIKE_PROBABILITY',
  [FEATURE_FLAGS.REPORTS]: 'REPORTS',
  [FEATURE_FLAGS.WATER_RISK]: 'WATER_RISK',
  [FEATURE_FLAGS.DISTRIBUTION_INTERRUPTIONS]: 'DISTRIBUTION_INTERRUPTIONS',
  [FEATURE_FLAGS.CHANGELOG_DIALOG]: 'CHANGELOG_DIALOG',
  [FEATURE_FLAGS.ALERT_REGISTRY_ALLOWLIST]: 'ALERT_REGISTRY_ALLOWLIST',
  [FEATURE_FLAGS.BIDDING_STRATEGY_SITE_LOAD]: 'BIDDING_STRATEGY_SITE_LOAD',
  [FEATURE_FLAGS.GRID_TARIFF_TIME_OF_USE]: 'GRID_TARIFF_TIME_OF_USE',
  [FEATURE_FLAGS.SHADOW_GRID_CONNECTION]: 'SHADOW_GRID_CONNECTION',
  [FEATURE_FLAGS.SHADOW_SITE]: 'SHADOW_SITE',
  [FEATURE_FLAGS.SHADOW_DEGRADATION]: 'SHADOW_DEGRADATION',
});

export function normalizeFeatureFlagValue(value) {
  if (typeof value !== 'string') return value;

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === 'true') return true;
  if (normalizedValue === 'false') return false;
  if (normalizedValue === INTERNAL_FEATURE_FLAG) return INTERNAL_FEATURE_FLAG;

  return value;
}

function getUserEmail(authState = {}) {
  return authState?.email || authState?.userEmail || null;
}

export function isInternalFeatureFlagUser(authState = {}) {
  const email = getUserEmail(authState);
  if (!email || typeof email !== 'string') return false;

  const normalizedEmail = email.trim().toLowerCase();
  return INTERNAL_FEATURE_EMAIL_DOMAINS.some((domain) => normalizedEmail.endsWith(domain));
}

export function isConfigFlagEnabled(flagValue, authState = {}) {
  const normalizedFlagValue = normalizeFeatureFlagValue(flagValue);

  if (authState?.isSuperUser === true) return true;
  if (normalizedFlagValue === false) return false;
  if (normalizedFlagValue === true) return true;
  if (normalizedFlagValue === INTERNAL_FEATURE_FLAG) {
    return isInternalFeatureFlagUser(authState);
  }

  return false;
}

export function isFeatureEnabled(featureKey, config = {}, authState = {}) {
  const configKey = ENTITLEMENT_TO_CONFIG[featureKey];
  const normalizedConfigValue = normalizeFeatureFlagValue(config?.[configKey]);
  const entitlements = Array.isArray(authState?.activeEntitlements)
    ? authState.activeEntitlements
    : [];

  // Super-users retain explicit override access regardless of rollout state.
  if (authState?.isSuperUser === true) return true;
  if (normalizedConfigValue === true) return true;
  if (normalizedConfigValue === INTERNAL_FEATURE_FLAG) {
    return isInternalFeatureFlagUser(authState);
  }

  // `false` or unset flags can still be enabled per user via entitlement.
  return entitlements.includes(featureKey);
}

export function isFeatureGloballyEnabled(featureKey, config = {}, authState = {}) {
  const configKey = ENTITLEMENT_TO_CONFIG[featureKey];
  return isConfigFlagEnabled(config?.[configKey], { ...authState, isSuperUser: false });
}
